'use client';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { forwardRef, type ReactNode } from 'react';
import { InlineTagChip, type InlineTagChipProps } from '../InlineTagChip';

export type ToolCallStatus = 'done' | 'ready' | 'pending' | 'failed';

export interface ToolCallChipProps extends Omit<InlineTagChipProps, 'icon' | 'label' | 'severity'> {
  /** Tool identifier (e.g. "credit.pull", "comps.auto_pull", "doc.draft_term_sheet"). */
  toolName: string;
  /** Execution status — drives the chip color. Default 'done'. */
  status?: ToolCallStatus;
  /** Optional run metadata (e.g. timestamp "3d ago", run ID). Rendered after a dot in the tooltip. */
  meta?: ReactNode;
}

const STATUS_SEVERITY: Record<ToolCallStatus, InlineTagChipProps['severity']> = {
  done: 'success',
  ready: 'primary',
  pending: 'neutral',
  failed: 'error',
};

/**
 * Inline chip referencing an Astor tool execution. Sparkle-prefixed monospace tag — same DNA as
 * CitationChip but used for tool-call attribution (e.g. `✦ credit.pull`).
 */
export const ToolCallChip = forwardRef<HTMLElement, ToolCallChipProps>(function ToolCallChip(
  { toolName, status = 'done', meta, tooltip, ariaLabel, ...rest },
  ref,
) {
  const composedTooltip =
    tooltip ??
    (meta ? (
      <>
        {toolName} · {meta}
      </>
    ) : (
      toolName
    ));

  return (
    <InlineTagChip
      ref={ref}
      label={toolName}
      icon={<AutoAwesomeIcon sx={{ fontSize: 'inherit' }} />}
      severity={STATUS_SEVERITY[status]}
      tooltip={composedTooltip}
      ariaLabel={ariaLabel ?? `Tool call: ${toolName}`}
      {...rest}
    />
  );
});

ToolCallChip.displayName = 'ToolCallChip';

export default ToolCallChip;
